import { useEffect, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { openBookingModal } from "@/components/booking-modal";
import { FAQS } from "@/data/faqs";

export default function Faq() {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState(false);

  useSeo({
    title: "FAQ — Часто задаваемые вопросы | Паруса на крыше",
    description: "Ответы на частые вопросы о ресторане Паруса на крыше: бронирование, скидка на день рождения, банкеты, меню, адрес и режим работы. Санкт-Петербург, ул. Марата, 5.",
    keywords: "вопросы ресторан Паруса на крыше, FAQ ресторан СПб, бронирование ресторан Петербург, скидка день рождения ресторан, банкет вопросы, режим работы Паруса на крыше",
    canonical: "/faq",
  });
  useBreadcrumbs([{ name: "Вопросы и ответы", path: "/faq" }]);

  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  // Inject FAQPage schema
  useEffect(() => {
    const allQA = FAQS.flatMap(cat => cat.items);
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": allQA.map(item => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a,
        },
      })),
    };
    let el = document.getElementById("faq-schema");
    if (!el) {
      el = document.createElement("script");
      el.id = "faq-schema";
      el.setAttribute("type", "application/ld+json");
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(schema);
    return () => { el?.remove(); };
  }, []);

  const toggle = (key: string) => {
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="w-full bg-background">
      {/* Hero */}
      <section className="relative py-32 border-b border-primary/10 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none select-none">
          <div className="absolute right-0 top-0 font-serif text-[400px] font-light leading-none text-primary/[0.03] tracking-tighter translate-x-16 -translate-y-16">?</div>
        </div>
        <div className="container mx-auto px-6 md:px-14 max-w-5xl relative z-10"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(20px)", transition: "all 0.8s ease 0.2s" }}>
          <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-6">Часто задаваемые вопросы</div>
          <h1 className="text-5xl md:text-7xl font-serif font-light text-foreground mb-8 leading-tight max-w-3xl">
            Вопросы о ресторане<br /><span className="text-accent italic">Паруса на крыше</span>
          </h1>
          <p className="text-muted-foreground font-light text-lg leading-relaxed max-w-xl mb-10">
            Собрали ответы на самые популярные вопросы о бронировании, меню, банкетах и особых поводах.
          </p>
          <button onClick={openBookingModal} className="border border-primary text-accent px-10 py-4 text-[10px] tracking-[0.3em] uppercase hover:bg-primary hover:text-background transition-colors">
            Забронировать стол
          </button>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6 md:px-14 max-w-4xl">
          <div className="space-y-16">
            {FAQS.map((cat) => (
              <div key={cat.category}>
                <div className="flex items-center gap-4 mb-8">
                  <div className="text-[8px] tracking-[0.4em] uppercase text-primary/50 shrink-0">{cat.category}</div>
                  <div className="flex-1 h-[1px] bg-primary/10" />
                </div>
                <div className="space-y-1">
                  {cat.items.map((item, idx) => {
                    const key = `${cat.category}-${idx}`;
                    const isOpen = openItems[key] ?? false;
                    return (
                      <div key={key} className="border border-primary/15 overflow-hidden group hover:border-primary/30 transition-colors">
                        <button
                          onClick={() => toggle(key)}
                          className="w-full flex items-start justify-between gap-6 px-7 py-6 text-left"
                          aria-expanded={isOpen}
                        >
                          <span className="font-serif text-lg font-light text-foreground group-hover:text-accent transition-colors leading-snug pr-4">
                            {item.q}
                          </span>
                          <span className={`text-primary/60 text-xl mt-0.5 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`}>
                            +
                          </span>
                        </button>
                        <div
                          className="overflow-hidden transition-all duration-300"
                          style={{ maxHeight: isOpen ? "400px" : "0px", opacity: isOpen ? 1 : 0 }}
                        >
                          <div className="px-7 pb-7 pt-1">
                            <div className="w-8 h-[1px] bg-primary/20 mb-4" />
                            <p className="text-muted-foreground font-light leading-relaxed text-sm">{item.a}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still have questions */}
      <section className="py-24 bg-card border-t border-primary/10">
        <div className="container mx-auto px-6 text-center max-w-2xl">
          <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-5">Не нашли ответ?</div>
          <h2 className="text-3xl md:text-4xl font-serif font-light text-foreground mb-6">Спросите нас напрямую</h2>
          <p className="text-muted-foreground font-light mb-10 leading-relaxed">
            Позвоните, напишите во ВКонтакте или просто зайдите в гости — мы рады ответить на любые вопросы.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+78129911090" className="bg-primary/20 border border-primary text-accent px-10 py-4 text-[10px] tracking-[0.3em] uppercase hover:bg-primary hover:text-background transition-colors">
              +7 (812) 991-10-90
            </a>
            <a href="https://vk.com/parusa_nakryshe" target="_blank" rel="noopener noreferrer" className="border border-primary/30 text-muted-foreground px-10 py-4 text-[10px] tracking-[0.3em] uppercase hover:border-primary hover:text-accent transition-colors">
              ВКонтакте →
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
